import React, { Component, useState, useEffect } from 'react'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import Toggle from '~/Common-Objects/UI/Forms/Toggle/Toggle'
import i18n from '~/i18n'
import Expander from '~/Common-Objects/Expander'
import axios from '~/data/http/axios'
import { useSelector } from 'react-redux'
import cloneDeep from 'lodash/cloneDeep'
import set from 'lodash/set'
import get from 'lodash/get'
import TemplateImageLoader from '~/pages/cp/automation/addNew/templateImageLoader.jsx'
import SiteVars from '~/Classes/siteVars'

const CAPSULE_FIELDS = [
    { value: '_capsule_firstName', label: 'First Name' },
    { value: '_capsule_lastName', label: 'Last Name' }
]

export default function ElementEditor(props) {
    const sitemaster = useSelector((state) => {
        return state.siteMaster
    })
    const accountMaster = useSelector((state) => {
        return state.accountMaster
    })

    const [groups, setGroups] = useState([])
    const [groupId, setGroupId] = useState(null)
    const [formId, setFormId] = useState(null)
    const [loading, setLoading] = useState(true)
    const [landingPages, setLandingPages] = useState([])
    const [formData, setFormData] = useState({})
    const [forcedUpdate, setForcedUpdate] = useState(0)
    const [formErrors, setFormErrors] = useState({})
    const [isCapsuleGroup, setIsCapsuleGroup] = useState(false)
    const [capsuleOwners, setCapsuleOwners] = useState([])

    useEffect(() => {
        if (!props.isDragging) {
            initialise()
        }
    }, [])

    useEffect(() => {
        if (!props.isDragging) {
            initialise()
        }
    }, [props.isDragging])

    useEffect(() => {
        if (isCapsuleGroup) {
            getCapsuleData()
        }
    }, [isCapsuleGroup])

    const getCapsuleData = async () => {
        let owners = []
        try {
            owners = (await axios.get('/application/capsule/users?skipOwning=true')).data
        } catch (error) {}
        let teams = []
        try {
            teams = (await axios.get('/application/capsule/teams')).data
        } catch (error) {}

        let finalData = []
        finalData.push(
            ...owners.map((d) => {
                d.value = 'owner-' + d.value
                return d
            })
        )
        finalData.push(
            ...teams.map((d) => {
                d.value = 'team-' + d.value
                return d
            })
        )
        setCapsuleOwners(finalData)
    }

    // The editor panel renders from a snapshot of the element taken when it was selected, so it
    // needs telling whenever we write to the element outside of the panel's own updateVal flow.
    const refreshElementEditor = (elementsData) => {
        if (props.refreshElement) props.refreshElement(elementsData)
    }

    const checkIfGroupIsCapsule = (groupId, groupsList = groups) => {
        let selectedGroup = groupsList.find((group) => group.value === groupId)
        let isCapsule = selectedGroup ? selectedGroup.isCapsule : false
        return isCapsule
    }

    const addCapsuleFieldsToElement = (elementsData) => {
        let inputFields = get(elementsData, `${props.elementPath}.localVariables.inputFields.value`)
        if (!Array.isArray(inputFields)) inputFields = []
        CAPSULE_FIELDS.forEach(({ value, label }) => {
            const existing = inputFields.find((field) => field.value === value)
            if (existing) {
                existing.required = true
                existing.cantDelete = true
                return
            }
            inputFields.push({
                cantDelete: true,
                fieldType: 'TEXT',
                label,
                options: [],
                required: true,
                value
            })
        })

        set(elementsData, `${props.elementPath}.localVariables.inputFields.value`, inputFields)
    }

    // Capsule name fields are only locked while a Capsule group is selected, so moving to
    // another group hands control of them back to the user.
    const unlockCapsuleFieldsOnElement = (elementsData) => {
        let inputFields = get(elementsData, `${props.elementPath}.localVariables.inputFields.value`)
        if (!Array.isArray(inputFields)) return
        inputFields.forEach((field) => {
            if (CAPSULE_FIELDS.some((capsuleField) => capsuleField.value === field.value)) field.cantDelete = false
        })
        set(elementsData, `${props.elementPath}.localVariables.inputFields.value`, inputFields)
    }

    const initialise = async () => {
        try {
            //get all groups
            const response = await axios.get(`/group/simple`)
            let groupsResp = response.data.Groups.map((group) => {
                return {
                    value: group.id,
                    label: group.groupName,
                    isCapsule: group.Applications?.find((app) => app.ApplicationId === 8) ? true : false
                }
            })
            setGroups(groupsResp)

            const landingPageResponse = await axios.get(`/landingPage`)
            let landingPagesResp = landingPageResponse.data.landingPages.map((landingPage) => {
                return {
                    value: landingPage.id,
                    label: landingPage.formName,
                    url: landingPage.url
                }
            })
            setLandingPages(landingPagesResp)

            if (props.localVariables.formId.value) {
                const formResponse = await axios.get(
                    `/group/${props.localVariables.groupId.value}/form/${props.localVariables.formId.value}`
                )
                setFormData(formResponse.data.Form)
            } else if (groupsResp.length > 0) {
                // New form dropped on stage: auto-create against the first available group
                await createForm(groupsResp[0].value, groupsResp)
                return
            }

            let isCapsule = checkIfGroupIsCapsule(props?.localVariables?.groupId?.value, groupsResp)
            setIsCapsuleGroup(isCapsule)

            setGroupId(props?.localVariables?.groupId?.value)
            setFormId(props?.localVariables?.formId?.value)

            setLoading(false)
        } catch (error) {
            console.error(error)
            setLoading(false)
        }
    }

    const createForm = async (groupId, groupsList = groups) => {
        setLoading(true)
        try {
            var form = {
                formName: '',
                wizard: {
                    step1: true
                },
                undefined,
                ThemeId: undefined,
                formType: 11,
                redirect: 0,
                parentFormId: Number(props.landingPageId)
            }

            let newForm = await axios.post('/group/' + groupId + '/form', form)
            let newFormid = newForm.data.Form.id

            const elementsData = cloneDeep(props.elementsData)
            set(elementsData, `${props.elementPath}.localVariables.groupId.value`, groupId)
            set(elementsData, `${props.elementPath}.localVariables.formId.value`, newFormid)

            // Email comes from the Form component defaults and is the only field we seed,
            // unless the group is Capsule connected and needs the Capsule name fields too.
            let isCapsule = checkIfGroupIsCapsule(groupId, groupsList)
            if (isCapsule) {
                addCapsuleFieldsToElement(elementsData)
            }

            await props.updateElementsData(elementsData, props.elementPath)
            refreshElementEditor(elementsData)

            setGroupId(groupId)
            setFormId(newFormid)
            setIsCapsuleGroup(isCapsule)
            setFormData(newForm.data.Form)
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    const changeFormSetting = async (tmpSetting, tmpValue, save) => {
        formData[tmpSetting] = tmpValue
        setFormData(formData)
        setForcedUpdate(forcedUpdate + 1)
        if (save) {
            await saveForm(formData)
        }
    }

    const changeFormOptionSetting = async (setting, value, save) => {
        let toUpdateArr = [[setting, value, save]]

        if (setting === 'mpArray') {
            toUpdateArr = value
        }

        for (let i = 0; i < toUpdateArr.length; i++) {
            let tmpSetting = toUpdateArr[i][0]
            let tmpValue = toUpdateArr[i][1]

            if (save) {
                if (tmpSetting == 'updateRedirectUrl' && typeof tmpValue === 'string') {
                    if (tmpValue.indexOf('http://') == -1 && tmpValue.indexOf('https://') == -1) {
                        tmpValue = 'https://' + tmpValue
                    }
                }
            }
            formData.options[tmpSetting] = tmpValue
        }
        setFormData(formData)
        setForcedUpdate(forcedUpdate + 1)
        if (save) {
            await saveForm(formData)
        }
    }

    const saveForm = async (formData) => {
        let formErrors = {}
        const redirectUrl =
            typeof formData.options.updateRedirectUrl === 'string' ? formData.options.updateRedirectUrl.trim() : ''

        if (formData.updateExisting === false) {
            if (formData.options.contactExistsMessage == '') formErrors.contactExistsMessage = 'Message is required'
        }

        if (formData.options.onUpdate == 'message' && formData.options.updateMessage == '')
            formErrors.updateMessage = 'Message is required'
        if (formData.options.onUpdate == 'message' && formData.options.createMessage == '')
            formErrors.createMessage = 'Message is required'

        if (
            formData.options.onUpdate == 'redirect' &&
            formData.options.redirectType == 'landingPage' &&
            !formData.options.redirectLandingPageId
        )
            formErrors.redirectLandingPageId = 'Landing page is required'
        if (formData.options.onUpdate == 'redirect' && formData.options.redirectType == 'website') {
            if (!redirectUrl) formErrors.updateRedirectUrl = 'Website is required'
            else if (!redirectUrl.startsWith('https://') && !redirectUrl.startsWith('http://'))
                formErrors.updateRedirectUrl = 'Website must start with https://'
            else if (redirectUrl.indexOf('.') == -1) formErrors.updateRedirectUrl = 'Invalid website address'
        }

        if (formData.sendConfirmation && formData.confirmationEmail == '')
            formErrors.confirmationEmail = 'Email is required'
        if (formData.sendConfirmation && formData.confirmationEmail && !formData.confirmationEmail.includes('@'))
            formErrors.confirmationEmail = 'Email must contain @ symbol'
        if (formData.sendConfirmation && formData.confirmationEmail && !formData.confirmationEmail.includes('.'))
            formErrors.confirmationEmail = 'Email must contain . symbol'

        if (formData.sendConfirmation && formData.confirmationEmail) {
            const domainCheck = (await axios.get('/accountMaster/domain/check?email=' + formData.confirmationEmail))
                .data
            if (!domainCheck?.Domain?.verified) {
                formErrors.confirmationEmail = 'Email domain is not verified'
            }
        }

        setFormErrors(formErrors)

        const response = await axios.put('/group/' + groupId + '/form/' + formId, formData)
        setFormData(response.data.Form)

        if (
            props.elementPath &&
            props.updateElementsData &&
            get(props.elementsData, `${props.elementPath}.elements[0]`)
        ) {
            const elementsData = cloneDeep(props.elementsData)
            set(elementsData, `${props.elementPath}.elements[0].contentDate`, Date.now())
            props.updateElementsData(elementsData, props.elementPath)
            refreshElementEditor(elementsData)
        }
    }

    const updateGroupId = async (newGroupId) => {
        setGroupId(newGroupId)
        formData.GroupId = newGroupId
        const response = await axios.put('/group/' + groupId + '/form/' + formId, formData)
        setFormData(response.data.Form)
        const isCapsule = checkIfGroupIsCapsule(newGroupId)
        setIsCapsuleGroup(isCapsule)
        const elementsData = cloneDeep(props.elementsData)
        if (isCapsule) {
            addCapsuleFieldsToElement(elementsData)
        } else {
            unlockCapsuleFieldsOnElement(elementsData)
        }
        set(elementsData, `${props.elementPath}.localVariables.groupId.value`, newGroupId)
        set(elementsData, `${props.elementPath}.contentDate`, Date.now())
        await props.updateElementsData(elementsData, props.elementPath)
        refreshElementEditor(elementsData)
    }

    const editTemplate = async (templateId, type) => {
        let redirectUrl = `/cp/templates/add/dragDropv2/?landingPageId=${props.landingPageId}`
        redirectUrl = encodeURIComponent(redirectUrl)

        props.saveTemplate(false, undefined, false, true)
        if (type == 'email') {
            window.open(
                `/cp/templates/add/dragDropv2/?form=false&groupId=${groupId}&formId=${formId}&page=confirm&redirect=${redirectUrl}&redirectType=windowSelf`,
                '_self'
            )
            return
        }
        if (type == 'oldLandingPage') {
            window.open(
                `/cp/templates/add/dragDropv2/?form=false&groupId=${groupId}&formId=${formId}&page=confirmed&redirect=${redirectUrl}&redirectType=windowSelf`,
                '_self'
            )
            return
        }
    }

    if (loading) {
        return (
            <div style={{ width: '100%' }}>
                <LoadingIcons iconType='2' />
            </div>
        )
    }

    if (!groupId) {
        return (
            <div style={{ width: '100%' }}>
                <div className='dndEditorv2-dd_toolHolder mar-b15'>
                    <div class='txtInput_label'>Group</div>
                    <div className='text-xsml mar-b5'>Choose the Group that this form will add contacts to.</div>
                    <Select
                        label=''
                        name='groupId'
                        data={groups}
                        onChange={(event) => {
                            createForm(event.target.value)
                        }}
                        value={-1}
                        placeholder='Select a group'
                    />
                </div>
            </div>
        )
    }

    return (
        <div style={{ width: '100%' }}>
            <Expander
                isFirst={false}
                key={`category_contacts`}
                label={'Contacts'}
                expanded={true}
                onExpand={() => {}}
                onCollapse={() => {}}
                noMarginTop={true}
            >
                <div className='dndEditorv2-dd_toolHolder mar-b15'>
                    <div class='txtInput_label'>Group</div>
                    <div className='text-xsml mar-b5'>Choose the Group that these contacts will be added to.</div>
                    <Select
                        label=''
                        name='groupId'
                        data={groups}
                        onChange={(event) => {
                            updateGroupId(event.target.value)
                        }}
                        value={groupId}
                        placeholder='Select a group'
                    />
                </div>

                <div className='dndEditorv2-dd_toolHolder mar-b15'>
                    <div class='txtInput_label'>If already in {sitemaster.siteName}</div>
                    <Select
                        label=''
                        name='updateExisting'
                        data={[
                            { value: true, label: 'Allow and update contact' },
                            { value: false, label: 'Block submission' }
                        ]}
                        onChange={(event) => {
                            changeFormSetting('updateExisting', event.target.value, true)
                        }}
                        value={formData.updateExisting}
                        placeholder='Select an action'
                    />
                </div>

                {formData.updateExisting === false && (
                    <div className='dndEditorv2-dd_toolHolder mar-b15'>
                        <div class='txtInput_label'>Message</div>
                        <TextInput
                            label=''
                            name='contactExistsMessage'
                            error={formErrors.contactExistsMessage}
                            value={formData.options.contactExistsMessage}
                            placeholder="It looks like you've already signed up."
                            onChange={(event) => {
                                changeFormOptionSetting('contactExistsMessage', event.target.value, false)
                            }}
                            onBlur={(event) => {
                                changeFormOptionSetting('contactExistsMessage', event.target.value, true)
                            }}
                        />
                    </div>
                )}
            </Expander>

            {isCapsuleGroup && (
                <Expander
                    isFirst={false}
                    key={`category_capsule`}
                    label={'Capsule'}
                    expanded={false}
                    onExpand={() => {}}
                    onCollapse={() => {}}
                    noMarginTop={false}
                >
                    <div className='dndEditorv2-dd_toolHolder mar-b15'>
                        <Select
                            outerClassName='mar-b25'
                            label='Capsule Assigned User or Team'
                            placeholder='Integration Owner'
                            data={capsuleOwners}
                            value={undefined}
                            onChange={(event) => {
                                changeFormOptionSetting('capsuleOwner', event.target.value, true)
                            }}
                            explainer='Signups will be added to Capsule. Select which user/team to assign them to'
                        />
                    </div>
                </Expander>
            )}

            <Expander
                isFirst={false}
                key={`category_afterSubmission`}
                label={'After Submission'}
                expanded={false}
                onExpand={() => {}}
                onCollapse={() => {}}
                noMarginTop={false}
            >
                <div className='dndEditorv2-dd_toolHolder mar-b15'>
                    <div class='txtInput_label'>Confirmation Type</div>
                    <Select
                        label=''
                        name='onUpdate'
                        data={[
                            { value: 'message', label: 'Show Message' },
                            { value: 'redirect', label: 'Redirect to URL' }
                        ]}
                        onChange={(event) => {
                            const nextType = event.target.value
                            const updates = [
                                ['onUpdate', nextType],
                                ['onCreate', nextType]
                            ]
                            if (nextType === 'redirect' && !formData.options.redirectType) {
                                updates.push(['redirectType', 'website'])
                            }
                            changeFormOptionSetting('mpArray', updates, true)
                        }}
                        value={formData.options.onUpdate}
                        placeholder='Select a confirmation type'
                    />
                </div>

                {formData.options.onUpdate == 'message' && (
                    <>
                        <div className='dndEditorv2-dd_toolHolder mar-b15'>
                            <div class='txtInput_label'>If they are a new contact</div>
                            <TextInput
                                label=''
                                name='createMessage'
                                error={formErrors.createMessage}
                                value={formData.options.createMessage}
                                placeholder='Thank you for signing up.'
                                onChange={(event) => {
                                    changeFormOptionSetting('createMessage', event.target.value, false)
                                }}
                                onBlur={(event) => {
                                    changeFormOptionSetting('mpArray', [['createMessage', event.target.value]], true)
                                }}
                            />
                        </div>
                        <div className='dndEditorv2-dd_toolHolder mar-b15'>
                            <div class='txtInput_label'>If they are an existing contact</div>
                            <TextInput
                                label=''
                                name='updateMessage'
                                error={formErrors.updateMessage}
                                value={formData.options.updateMessage}
                                placeholder='You have already signed up, we have updated your details'
                                onChange={(event) => {
                                    changeFormOptionSetting('updateMessage', event.target.value, false)
                                }}
                                onBlur={(event) => {
                                    changeFormOptionSetting('mpArray', [['updateMessage', event.target.value]], true)
                                }}
                            />
                        </div>
                    </>
                )}

                {formData.options.onUpdate == 'redirect' && (
                    <>
                        <div className='dndEditorv2-dd_toolHolder mar-b15'>
                            <div class='txtInput_label'>Link To</div>
                            <Select
                                label=''
                                name='redirectType'
                                data={[
                                    { value: 'website', label: 'Website' },
                                    { value: 'landingPage', label: 'Another Landing Page' }
                                ]}
                                onChange={(event) => {
                                    changeFormOptionSetting('redirectType', event.target.value, true)
                                }}
                                value={formData.options.redirectType}
                                placeholder='Select what to link to'
                            />
                        </div>
                        {formData.options.redirectType == 'website' && (
                            <div className='dndEditorv2-dd_toolHolder mar-b15'>
                                <div class='txtInput_label'>Website</div>
                                <TextInput
                                    label=''
                                    name='updateRedirectUrl'
                                    error={formErrors.updateRedirectUrl}
                                    value={formData.options.updateRedirectUrl}
                                    placeholder='https://www.example.com'
                                    onChange={(event) => {
                                        changeFormOptionSetting('updateRedirectUrl', event.target.value, false)
                                    }}
                                    onBlur={(event) => {
                                        changeFormOptionSetting(
                                            'mpArray',
                                            [
                                                ['updateRedirectUrl', event.target.value],
                                                ['createRedirectUrl', event.target.value]
                                            ],
                                            true
                                        )
                                    }}
                                />
                            </div>
                        )}
                        {formData.options.redirectType === 'landingPage' && (
                            <div className='dndEditorv2-dd_toolHolder mar-b15'>
                                <div class='txtInput_label'>Landing Page</div>
                                <Select
                                    label=''
                                    name='redirectLandingPageId'
                                    error={formErrors.redirectLandingPageId}
                                    data={landingPages}
                                    filter={true}
                                    onChange={(event) => {
                                        let selectedLandingPage = landingPages.find(
                                            (landingPage) => landingPage.value == event.target.value
                                        )
                                        changeFormOptionSetting(
                                            'mpArray',
                                            [
                                                ['redirectLandingPageId', selectedLandingPage.value],
                                                ['updateRedirectUrl', selectedLandingPage.url],
                                                ['createRedirectUrl', selectedLandingPage.url]
                                            ],
                                            true
                                        )
                                    }}
                                    value={formData.options.redirectLandingPageId}
                                    placeholder='Select a landing page'
                                />
                            </div>
                        )}
                        {(formData.options.confirmationLinkTo == 'website' ||
                            formData.options.confirmationLinkTo == 'landingPage') && (
                            <div className='dndEditorv2-dd_toolHolder mar-b15'>
                                <Toggle
                                    name='confirmationLinkToOpenInNewTab'
                                    after={'Open in new tab'}
                                    value={!!formData.options.confirmationLinkToOpenInNewTab}
                                    onChange={(event) => {
                                        changeFormOptionSetting(
                                            'confirmationLinkToOpenInNewTab',
                                            event.target.checked ? true : false,
                                            true
                                        )
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            </Expander>

            <Expander
                isFirst={false}
                key={`category_doubleOptIn`}
                label={'Double Opt-In'}
                expanded={false}
                onExpand={() => {}}
                onCollapse={() => {}}
                noMarginTop={false}
            >
                <div className='dndEditorv2-dd_toolHolder mar-b15'>
                    <Toggle
                        name='sendConfirmation'
                        after={'Enable Double Opt-In'}
                        afterSecondary={'Contacts must confirm their email before being added to your group'}
                        value={!!formData.sendConfirmation}
                        onChange={(event) => {
                            changeFormSetting('sendConfirmation', event.target.checked ? true : false, true)
                        }}
                    />
                </div>
                {formData.sendConfirmation && (
                    <>
                        <div className='dndEditorv2-dd_toolHolder mar-b15'>
                            <div class='txtInput_label'>Email Subject</div>
                            <TextInput
                                label=''
                                name='confirmationSubject'
                                value={formData.confirmationSubject}
                                placeholder='Enter the subject of the email'
                                onChange={(event) => {
                                    changeFormSetting('confirmationSubject', event.target.value, false)
                                }}
                                onBlur={(event) => {
                                    changeFormSetting('confirmationSubject', event.target.value, true)
                                }}
                            />
                        </div>

                        <div className='dndEditorv2-dd_toolHolder mar-b15'>
                            <div class='txtInput_label'>From Name</div>
                            <TextInput
                                label=''
                                name='confirmationName'
                                value={formData.confirmationName}
                                placeholder='Enter the name of the sender'
                                onChange={(event) => {
                                    changeFormSetting('confirmationName', event.target.value, false)
                                }}
                                onBlur={(event) => {
                                    changeFormSetting('confirmationName', event.target.value, true)
                                }}
                            />
                        </div>

                        <div className='dndEditorv2-dd_toolHolder mar-b15'>
                            <div class='txtInput_label'>From Address</div>
                            <TextInput
                                label=''
                                name='confirmationEmail'
                                error={formErrors.confirmationEmail}
                                value={formData.confirmationEmail}
                                placeholder='Enter the email address this should come from'
                                onChange={(event) => {
                                    changeFormSetting('confirmationEmail', event.target.value, false)
                                }}
                                onBlur={(event) => {
                                    changeFormSetting('confirmationEmail', event.target.value, true)
                                }}
                            />
                        </div>

                        <div className='dndEditorv2-dd_toolHolder mar-b15'>
                            <div
                                className='dndEditorv2-dd_tool_contentPreviewHolder mar-b15'
                                onClick={() => {
                                    editTemplate(formData.formCreatedFormId, 'email')
                                }}
                                style={{ height: 140, cursor: 'pointer' }}
                            >
                                <TemplateImageLoader
                                    imageUrl={
                                        SiteVars.repoEndPoint +
                                        'userContent/' +
                                        accountMaster.accountMaster.id +
                                        '/templates/screenshots/form' +
                                        formData.formCreatedFormId +
                                        '.jpg?now=' +
                                        Date.now()
                                    }
                                    form={true}
                                />
                            </div>
                            <Button
                                onClick={() => {
                                    editTemplate(formData.formCreatedFormId, 'email')
                                }}
                                className='button-sml'
                            >
                                Open Editor
                            </Button>
                        </div>

                        <div className='dndEditorv2-dd_toolHolder mar-b15'>
                            <div class='txtInput_label'>Thank you page</div>
                            <div
                                className='dndEditorv2-dd_tool_contentPreviewHolder mar-b15'
                                onClick={() => {
                                    editTemplate(formData.formConfirmedFormId, 'oldLandingPage')
                                }}
                                style={{ height: 140, cursor: 'pointer' }}
                            >
                                <TemplateImageLoader
                                    imageUrl={
                                        SiteVars.repoEndPoint +
                                        'userContent/' +
                                        accountMaster.accountMaster.id +
                                        '/templates/screenshots/form' +
                                        formData.formConfirmedFormId +
                                        '.jpg?now=' +
                                        Date.now()
                                    }
                                    form={true}
                                />
                            </div>
                            <Button
                                onClick={() => {
                                    editTemplate(formData.formConfirmedFormId, 'oldLandingPage')
                                }}
                                className='button-sml'
                            >
                                Open Editor
                            </Button>
                        </div>
                    </>
                )}
            </Expander>

            <Expander
                isFirst={false}
                key={`category_security`}
                label={'Security'}
                expanded={false}
                onExpand={() => {}}
                onCollapse={() => {}}
                noMarginTop={false}
            >
                <div className='dndEditorv2-dd_toolHolder mar-b15'>
                    <Toggle
                        name='enableRecaptcha'
                        after={'Enable reCAPTCHA'}
                        afterSecondary={'Protect your form from spam and bots'}
                        value={!!formData.options.enableRecaptcha}
                        onChange={(event) => {
                            changeFormOptionSetting('enableRecaptcha', event.target.checked ? true : false, true)
                        }}
                    />
                </div>
            </Expander>
        </div>
    )
}
